import { FC, useMemo } from 'react'
import styles from 'components/features/socialFeed/ComposeActions/ComposeActions.styl'
import cn from 'classnames'
import Button from 'components/features/pfm/Button/Button'
import { createComposeImagesSelector } from 'selectors/feedComposeSelectors'
import { ComposeId } from 'interfaces/feed'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { AddImageButton } from 'components/features/socialFeed/ComposeActions/AddImageButton'
import { AddEmojiButton } from 'components/features/socialFeed/ComposeActions/AddEmojiButton'
import { SubmitButton } from 'components/features/socialFeed/ComposeActions/SubmitButton'
import { IS_STATUS_APP, IS_MOBILE } from 'helpers/userAgentHelpers'
import { getAllowedButtons } from 'helpers/composeHelpers'
import { useComposeState } from 'helpers/hookHelpers'

type ComposeActionsProps = {
  composeId: ComposeId
  onCancel?: () => void
}

const DEFAULT_COMPOSE_OBJ = {
  isSubmitting: null,
  isEdit: null,
  replyCommentUid: null,
  isOpportunityReview: false,
}

export const ComposeActions: FC<ComposeActionsProps> = (props) => {
  const {
    composeId,
    onCancel,
  } = props

  const {
    isSubmitting,
    isEdit,
    isOpportunityReview,
    replyCommentUid,
  } = useComposeState(composeId, DEFAULT_COMPOSE_OBJ)

  const composeImagesSelector = useMemo(createComposeImagesSelector, [])
  const images = useStoreSelector((state) => composeImagesSelector(state, props))

  const isImageUploading = useMemo(() => {
    return !!images.find((imageState) => imageState.isLoading)
  }, [images])

  const isComposeSubmitting = useMemo(() => {
    return Boolean(isSubmitting) || isImageUploading
  }, [isSubmitting, isImageUploading])


  const allow = useMemo(() => {
    const isMobile = (IS_MOBILE || IS_STATUS_APP)
    return getAllowedButtons({ isEdit, isMobile, replyCommentUid, isOpportunityReview })
  }, [isEdit, replyCommentUid, isOpportunityReview])

  const className = useMemo(() => {
    return cn(styles.composeActions, {
      [styles.withCancel]: allow.cancel,
    })
  }, [allow.cancel])

  return (
    <div className={ className }>
      <div className={ styles.actions }>
        { !allow.cancel && <span className={ styles.label }>Add:</span> }
        <AddImageButton composeId={ composeId } />
        { allow.emoji && <AddEmojiButton composeId={ composeId } /> }
      </div>
      <div className={ styles.cta }>
        { allow.cancel &&
          <div className={ styles.cancel }>
            <Button
              size='mini'
              submit={ false }
              style='textOnly'
              onClick={ onCancel }
            >
              Cancel
            </Button>
          </div>}
        { allow.post && (
          <SubmitButton
            composeId={ composeId }
            text='Post'
            isDisabled={ isComposeSubmitting }
          />
        )}
        { allow.reply && (
          <SubmitButton
            composeId={ composeId }
            text='Reply'
            isDisabled={ isComposeSubmitting }
          />
        )}
        { allow.save && (
          <SubmitButton
            composeId={ composeId }
            text='Save'
            isDisabled={ isComposeSubmitting }
          />
        )}
      </div>
    </div>
  )
}
